import { useCallback, useEffect, useMemo } from 'react'
import { useClient, useQuery } from 'urql'
import { getFragmentData } from '../../gql'
import { GetTimeClockQuery, TimeClockFragment } from '../../graphql/timeclock'
import { parseDatetime } from '../../helpers/datetime'
import { getIsOnline } from '../../helpers/networkStatus'
import { NETWORK_ONLY } from '../../helpers/requestPolicy'

/** v1 parity: Main.tsx polled the time clock every 5 minutes. */
const POLL_INTERVAL_MS = 5 * 60 * 1000

export type TimeClockStatus = 'clockedOut' | 'clockedIn' | 'onBreak'

const TIME_CLOCK_STATUS_MAP: Record<
  TimeClockStatus,
  { slug: TimeClockStatus; name: string; isActive: boolean }
> = {
  clockedIn: { slug: 'clockedIn', name: 'Clocked In', isActive: true },
  clockedOut: { slug: 'clockedOut', name: 'Clocked Out', isActive: false },
  onBreak: { slug: 'onBreak', name: 'On Break', isActive: true },
}

/**
 * Current time clock state. `poll: true` refreshes every 5 min — pass it from
 * ONE always-mounted consumer (TabLayout) only.
 */
export const useTimeClock = (options?: { pause?: boolean; poll?: boolean }) => {
  const client = useClient()
  const [result, reexecute] = useQuery({
    query: GetTimeClockQuery,
    pause: options?.pause,
    requestPolicy: 'cache-and-network',
  })

  const { pause, poll } = options ?? {}
  useEffect(() => {
    if (!poll || pause) return
    const id = setInterval(() => {
      // skip while backgrounded/offline
      if (document.visibilityState !== 'visible') return
      if (!getIsOnline()) return
      reexecute({ requestPolicy: 'network-only' })
    }, POLL_INTERVAL_MS)
    return () => clearInterval(id)
  }, [poll, pause, reexecute])

  const rawData = result.data?.me
  const rawTimeClock =
    rawData && 'timeClock' in rawData ? rawData.timeClock : null

  const timeClock = rawTimeClock
    ? getFragmentData(TimeClockFragment, rawTimeClock)
    : null

  const statusSlug = useMemo((): TimeClockStatus => {
    if (!timeClock) return 'clockedOut'
    if (timeClock.endTime) return 'clockedOut'
    if (timeClock.task?.payable === false) return 'onBreak'
    return 'clockedIn'
  }, [timeClock])

  const status = TIME_CLOCK_STATUS_MAP[statusSlug]

  const data = useMemo(() => {
    if (!timeClock) return null
    const start = parseDatetime(timeClock.startTime)
    const end = parseDatetime(timeClock.endTime)
    const shiftStart = parseDatetime(timeClock.shift?.startTime)
    const shiftEnd = parseDatetime(timeClock.shift?.endTime)
    return {
      id: timeClock.id,
      status,
      task: timeClock.task,
      startTime: start?.toJSDate() ?? null,
      endTime: end?.toJSDate() ?? null,
      shift: {
        ...timeClock.shift,
        startTime: shiftStart?.toJSDate() ?? null,
        endTime: shiftEnd?.toJSDate() ?? null,
      },
    }
  }, [timeClock, status])

  // Unlike the fire-and-forget poll, this must await settlement (CRE-102)
  const refetch = useCallback(
    () => client.query(GetTimeClockQuery, {}, NETWORK_ONLY).toPromise(),
    [client],
  )

  return {
    data,
    status,
    loading: result.fetching,
    error: result.error,
    refetch,
  }
}
